import {
  DragOutlined,
  EditFilled,
  MinusOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import { ButtonDeleteConfirm, ButtonDeleteConfirmProps } from '@framework/ui';
import { Button, Space } from 'antd';
import clx from 'classnames';
import React from 'react';
import styled from 'styled-components';

const ContentWrapper = styled.div`
  position: relative;
  // min-height: 44px;
  &.is-space-for-drag {
    opacity: 0;
  }
`;
const IconDragWrapper = styled.span`
  position: relative;
  font-size: 40px;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: #fff;
  color: ${props => props.theme.colorPrimary};
`;

const HoverWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  overflow: hidden;
  justify-content: flex-end;
  &.is-dragging {
    justify-content: center;
  }
  align-items: center;
  padding: 2px 24px;
  margin: -8px;
  background-color: rgba(255, 255, 255, 0);
  transition: all 0.3s;
  border: 2px dashed transparent;
  border-radius: 12px;
  .hover-content {
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0px 0px 14px 10px #fff;
    opacity: 0;
    transition-delay: 0.1s;
    transition: opacity 0.2s;
  }
  &:hover {
    border: 2px dashed ${props => props.theme.colorPrimary};
    background-color: rgba(255, 255, 255, 0.5);
    &.is-dragging {
      background-color: rgba(255, 255, 255, 0.25);
      border-color: ${props => props.theme.colorBorder};
    }
    &.grabbable {
      cursor: grab;
      &.is-dragging {
        cursor: grabbing;
      }
    }
    .hover-content {
      opacity: 1;
    }
  }
`;

export interface ContentHoverEditButtonProps {
  children: React.ReactNode;
  isContainer?: boolean;
  configMode?: boolean;
  isDragging?: boolean;
  isSpaceForDrag?: boolean;
  onEdit?: React.MouseEventHandler<HTMLElement>;
  onAdd?: React.MouseEventHandler<HTMLElement>;
  onRemove?: React.MouseEventHandler<HTMLElement>;
  onConfig?: React.MouseEventHandler<HTMLElement>;
  confirmRemove?: boolean;
  confirmRemoveProps?: Omit<ButtonDeleteConfirmProps, 'onConfirm'>;
  grabbable?: boolean;
}

export function ContentHoverEditButton(props: ContentHoverEditButtonProps) {
  const {
    configMode,
    isDragging,
    isSpaceForDrag,
    children,
    isContainer,
    onEdit,
    onAdd,
    onConfig,
    confirmRemove,
    confirmRemoveProps,
    onRemove,
    grabbable = true,
  } = props;

  const { t } = useTranslation();

  return (
    <ContentWrapper className={clx({ 'is-space-for-drag': isSpaceForDrag })}>
      {isContainer && (
        <div>
          <Space>
            {onRemove &&
              (confirmRemove ? (
                <ButtonDeleteConfirm
                  {...confirmRemoveProps}
                  size="middle"
                  onConfirm={onRemove as any}
                />
              ) : (
                <Button icon={<MinusOutlined />} onClick={onRemove} />
              ))}
            {onEdit && (
              <Button type="primary" icon={<EditFilled />} onClick={onEdit}>
                {t('button.content-hover-edit', 'Modifica')}
              </Button>
            )}
            {onAdd && (
              <Button
                icon={<PlusOutlined aria-hidden="true" />}
                onClick={onAdd}
              />
            )}
            {onConfig && (
              <Button icon={<SettingOutlined />} onClick={onConfig} />
            )}
          </Space>
        </div>
      )}
      {(configMode ?? isDragging) && (
        <HoverWrapper className={clx({ 'is-dragging': isDragging, grabbable })}>
          <Space className="hover-content">
            {isDragging ? (
              <IconDragWrapper>
                <DragOutlined />
              </IconDragWrapper>
            ) : (
              <Space>
                {onRemove &&
                  (confirmRemove ? (
                    <ButtonDeleteConfirm
                      {...confirmRemoveProps}
                      size="middle"
                      onConfirm={onRemove as any}
                    />
                  ) : (
                    <Button icon={<MinusOutlined />} onClick={onRemove} />
                  ))}
                {onEdit && (
                  <Button type="primary" icon={<EditFilled />} onClick={onEdit}>
                    {t('button.content-hover-edit-config-mode', 'Modifica')}
                  </Button>
                )}
                {onAdd && (
                  <Button
                    icon={<PlusOutlined aria-hidden="true" />}
                    onClick={onAdd}
                  />
                )}
                {onConfig && (
                  <Button icon={<SettingOutlined />} onClick={onConfig} />
                )}
              </Space>
            )}
          </Space>
        </HoverWrapper>
      )}
      {children}
    </ContentWrapper>
  );
}
